// no default React import needed with react-jsx
import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  
  Grid,
  Card,
  CardContent,
  CardHeader,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  // LinearProgress,
  Alert,
  Button,
} from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Storage as StorageIcon,
  Cloud as CloudIcon,
  Memory as MemoryIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material'
import { SystemHealth } from '../types/api'
import { adminApi } from '../lib/sdkClient'

// Will show N/A until backend provides metrics/logs endpoints
// const systemMetrics = { cpu_usage: undefined as any, memory_usage: undefined as any, disk_usage: undefined as any, network_io: undefined as any }
const errorLogs: any[] = []

export default function SystemHealthPage() {
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null)
  const [loading, setLoading] = useState(true)
  const [lastCheck, setLastCheck] = useState<Date>(new Date())

  const fetchSystemHealth = async () => {
    setLoading(true)
    try {
      const { data } = await adminApi.adminSystemHealthArtrackAdminSystemHealthGet()
      setSystemHealth(data as any)
      setLastCheck(new Date())
    } catch (error: any) {
      console.error('Error fetching system health:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSystemHealth()
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchSystemHealth, 60000)
    return () => clearInterval(interval)
  }, [])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'success'
      case 'degraded': return 'warning'
      case 'down': return 'error'
      default: return 'default'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircleIcon />
      case 'degraded': return <WarningIcon />
      case 'down': return <ErrorIcon />
      default: return <CheckCircleIcon />
    }
  }

  // const getProgressColor = (value: number) => {
  //   if (value < 50) return 'success'
  //   if (value < 80) return 'warning'
  //   return 'error'
  // }

  const services = systemHealth ? [
    { name: 'API Server', status: systemHealth.api_status },
    { name: 'Database', status: systemHealth.database_status },
    { name: 'Storage Service', status: systemHealth.storage_status },
    { name: 'AI Analysis', status: systemHealth.ai_analysis_status },
    { name: 'Job Queue', status: systemHealth.queue_status },
  ] : []

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          System Health
        </Typography>
        
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchSystemHealth}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Last checked: {lastCheck.toLocaleString()}
      </Typography>

      {/* Service Status */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {services.map((service) => (
          <Grid item xs={12} sm={6} md={4} key={service.name}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="h6">{service.name}</Typography>
                  <Chip
                    icon={getStatusIcon(service.status)}
                    label={service.status}
                    color={getStatusColor(service.status)}
                    size="small"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* System Metrics */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardHeader title="System Metrics" />
            <CardContent>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <SpeedIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="CPU Usage"
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2">N/A</Typography>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'span' }}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <MemoryIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Memory Usage"
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2">N/A</Typography>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'span' }}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <StorageIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Disk Usage"
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2">N/A</Typography>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'span' }}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <CloudIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Network I/O"
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2">N/A</Typography>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'span' }}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Errors */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardHeader title="Recent Error Logs" />
            <CardContent>
              <List dense>
                {errorLogs.map((log) => (
                  <ListItem key={log.id}>
                    <ListItemIcon>
                      {log.level === 'error' ? (
                        <ErrorIcon color="error" />
                      ) : (
                        <WarningIcon color="warning" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={log.message}
                      secondary={
                        <Box>
                          <Typography variant="caption" color="textSecondary">
                            {log.endpoint} • {new Date(log.timestamp).toLocaleString()}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Alerts */}
        <Grid item xs={12}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>High disk usage detected:</strong> Storage is at 82% capacity. Consider cleaning up old files or expanding storage.
          </Alert>
          
          <Alert severity="info">
            <strong>Scheduled maintenance:</strong> Database maintenance is scheduled for tonight at 2:00 AM UTC.
          </Alert>
        </Grid>
      </Grid>
    </Box>
  )
}